/**
 * DocCallout
 *
 * Tinted boxed-out note. Variants: info (blue), tip (emerald),
 * warning (amber), danger (rose).
 */
import type { ReactNode } from 'react';
import { Info, AlertTriangle, Lightbulb, ShieldAlert } from 'lucide-react';
import { cn } from '@respectly/ui';

export type DocCalloutVariant = 'info' | 'tip' | 'warning' | 'danger';

export interface DocCalloutProps {
  variant?: DocCalloutVariant;
  title?: string;
  children: ReactNode;
  className?: string;
}

const VARIANT_STYLES: Record<
  DocCalloutVariant,
  {
    icon: typeof Info;
    container: string;
    iconColor: string;
    title: string;
  }
> = {
  info: {
    icon: Info,
    container:
      'border-blue-200/70 bg-blue-50/70 dark:border-blue-900/40 dark:bg-blue-950/30',
    iconColor: 'text-blue-600 dark:text-blue-400',
    title: 'text-blue-900 dark:text-blue-200',
  },
  tip: {
    icon: Lightbulb,
    container:
      'border-emerald-200/70 bg-emerald-50/70 dark:border-emerald-900/40 dark:bg-emerald-950/30',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    title: 'text-emerald-900 dark:text-emerald-200',
  },
  warning: {
    icon: AlertTriangle,
    container:
      'border-amber-200/70 bg-amber-50/70 dark:border-amber-900/40 dark:bg-amber-950/30',
    iconColor: 'text-amber-600 dark:text-amber-400',
    title: 'text-amber-900 dark:text-amber-200',
  },
  danger: {
    icon: ShieldAlert,
    container:
      'border-rose-200/70 bg-rose-50/70 dark:border-rose-900/40 dark:bg-rose-950/30',
    iconColor: 'text-rose-600 dark:text-rose-400',
    title: 'text-rose-900 dark:text-rose-200',
  },
};

export function DocCallout({
  variant = 'info',
  title,
  children,
  className,
}: DocCalloutProps) {
  const styles = VARIANT_STYLES[variant];
  const Icon = styles.icon;
  return (
    <div
      className={cn(
        'flex gap-3 rounded-xl border p-4 my-4',
        styles.container,
        className
      )}
    >
      <Icon
        className={cn('w-5 h-5 mt-0.5 flex-shrink-0', styles.iconColor)}
        aria-hidden="true"
      />
      <div className="flex-1 min-w-0">
        {title ? (
          <p className={cn('mb-1 font-semibold', styles.title)}>{title}</p>
        ) : null}
        <div className="text-sm text-slate-700 dark:text-slate-300 leading-6">
          {children}
        </div>
      </div>
    </div>
  );
}
